import { designTokens } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Divider } from "@itandi/itandi-bb-ui/Divider";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { StatusChip } from "@itandi/itandi-bb-ui/StatusChip";
import { toast } from "react-toastify";
import { WithdrawPartnerAssignmentModal } from "~/components/common/deals/show/main/WithdrawPartnerAssignmentModal";
import { WithdrawSelfAssignmentModal } from "~/components/common/deals/show/main/WithdrawSelfAssignmentModal";
import type { PartnerVendorSelectOption } from "~/components/common/vendors/PartnerVendorSelectModal";
import { PartnerVendorSelectModal } from "~/components/common/vendors/PartnerVendorSelectModal";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import { useDealPartnerAssignmentLatest } from "~/hooks/vendors/deals/useDealPartnerAssignments";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import { useVendorPartners } from "~/hooks/vendors/useVendorPartners";
import { getDeclineReasonLabel } from "~/models/DealPartnerAssignment";
import {
	getSubDealVendor,
	getSubVendor,
	isOriginVendor
} from "~/models/DealVendorCompany";
import { isCanceled, isCompleted } from "~/models/Status";
import type { VendorPartner } from "~/models/VendorPartner";
import { isSuccess } from "~/utils/api/api_handler";
import { create as postDealVendorCompany } from "~/utils/api/vendors/deals/DealVendorCompany";
import Styles from "./DealMainWorkflowVendorSub.module.scss";

const responseStatusChipProps = {
	accept: {
		label: "対応可能",
		variant: "success"
	},
	pending: {
		label: "回答待ち",
		variant: "warning"
	}
} as const;

function DeclinedLatestAssignmentMessage({ dealId }: { dealId: number }) {
	const { data: latestPartnerAssignment } =
		useDealPartnerAssignmentLatest(dealId);

	if (latestPartnerAssignment?.status !== "decline") {
		return null;
	}

	const declineReasonLabel =
		latestPartnerAssignment.declineReasonCategory === null
			? null
			: getDeclineReasonLabel(latestPartnerAssignment.declineReasonCategory);

	return (
		<>
			<Divider my={designTokens["spacing/m"]} />
			<Paragraph>
				<Label weight="bold">
					{`${latestPartnerAssignment.partnerVendorCompany.name}から対応不可の回答がありました。`}
				</Label>
			</Paragraph>
			{declineReasonLabel ? (
				<Paragraph mt={designTokens["spacing/xs"]} size="s">
					<Label weight="bold">理由：</Label>
					{declineReasonLabel}
				</Paragraph>
			) : null}
			{latestPartnerAssignment.declineReasonDetail ? (
				<Paragraph mt={designTokens["spacing/xs"]} size="s">
					<Label weight="bold">詳細：</Label>
					{latestPartnerAssignment.declineReasonDetail}
				</Paragraph>
			) : null}
		</>
	);
}

export function DealMainWorkflowVendorSub() {
	const { isOpen, handleOpen, handleClose } = useOpenHandler();
	const {
		isOpen: isWithdrawOpen,
		handleOpen: handleWithdrawOpen,
		handleClose: handleWithdrawClose
	} = useOpenHandler();
	const id = useIdQueryHandler();
	const { data: vendorPartners } = useVendorPartners().index();
	const { data: deal, mutate: mutateDeal } = useDeals().show(id);
	const { data: session } = useSessions().index();
	const { match: isSp } = useMediaQuery(MEDIA_QUERY.sp);
	if (
		deal === null ||
		vendorPartners === null ||
		session === null ||
		!isOriginVendor(deal, session.vendorCompany.id) ||
		isCompleted(deal.statusId) ||
		isCanceled(deal.statusId) ||
		!session.vendorCompany.usePartner
	) {
		return null;
	}

	const subVendor = getSubVendor(deal);
	const subDealVendor = getSubDealVendor(deal);
	const responseStatus = subDealVendor?.partnerAssignmentResponseStatus;
	const responseStatusChip = responseStatus
		? responseStatusChipProps[responseStatus]
		: null;

	const handleAcceptSelect = async (vendorCompanyId: number): Promise<void> => {
		const result = await postDealVendorCompany(deal.id, vendorCompanyId);
		if (isSuccess(result)) {
			await mutateDeal();
			handleClose();
			toast.success("依頼しました。");
			return;
		}

		toast.error(result.message ?? "エラーが発生しました。");
	};

	const partnerVendors: PartnerVendorSelectOption[] = vendorPartners.map(
		(v: VendorPartner) => {
			return {
				address: v.partnerVendorCompany.address,
				id: v.partnerVendorCompany.id,
				isSelf: false,
				name: v.partnerVendorCompany.name
			};
		}
	);

	const selfVendor: PartnerVendorSelectOption = {
		address: session.vendorCompany.address,
		id: session.vendorCompany.id,
		isSelf: true,
		name: session.vendorCompany.name
	};
	const isSelfAssigned = subVendor?.id === session.vendorCompany.id;

	return (
		<>
			{subVendor === null ? (
				<div className={Styles.UnSelectedSubVendorCard}>
					<div>
						<Flex
							alignItems="center"
							flexWrap="wrap"
							gap={designTokens["spacing/s"]}
							justifyContent="space-between"
						>
							<Flex
								czz={{ flex: 1, minWidth: 0 }}
								direction="column"
								gap={designTokens["spacing/xxs"]}
							>
								<p className={Styles.SubVendorName}>
									実施会社を選択してください
								</p>
								<Paragraph size="s">
									依頼せず直接実施する場合は自社を選択してください
								</Paragraph>
							</Flex>
							<Button
								onClick={handleOpen}
								variant="secondary"
								width={isSp ? "fill" : "default"}
							>
								実施会社を選択
							</Button>
						</Flex>
						<DeclinedLatestAssignmentMessage dealId={deal.id} />
					</div>
				</div>
			) : (
				<div className={Styles.SubVendorCard}>
					<Flex
						alignItems="center"
						flexWrap="wrap"
						gap={designTokens["spacing/s"]}
						justifyContent="space-between"
					>
						<Flex
							alignItems="center"
							czz={{ flex: 1, minWidth: 0 }}
							gap={designTokens["spacing/s"]}
						>
							<img
								alt="avatar"
								className={Styles.AvatarIcon}
								src="/assets/Avatar.svg"
							/>
							{isSelfAssigned ? (
								<div className={Styles.SubVendorName}>
									{session.vendorCompany.name} で実施
								</div>
							) : (
								<Flex
									alignItems="center"
									czz={{ minWidth: 0 }}
									flexWrap="wrap"
									gap={designTokens["spacing/xxs"]}
								>
									<span className={Styles.SubVendorNameText}>
										{subVendor.name} に依頼中
									</span>
									{responseStatusChip !== null ? (
										<StatusChip variant={responseStatusChip.variant}>
											{responseStatusChip.label}
										</StatusChip>
									) : null}
								</Flex>
							)}
						</Flex>
						<Button
							onClick={handleWithdrawOpen}
							variant="secondary"
							width={isSp ? "fill" : "default"}
						>
							{isSelfAssigned ? "自社での実施を解除" : "依頼の取り下げ"}
						</Button>
					</Flex>
				</div>
			)}
			{isOpen ? (
				<PartnerVendorSelectModal
					currentSelectedId={subVendor?.id ?? null}
					onAccept={handleAcceptSelect}
					onClose={handleClose}
					partnerVendors={partnerVendors}
					selfVendor={selfVendor}
				/>
			) : null}
			{isWithdrawOpen ? (
				isSelfAssigned ? (
					<WithdrawSelfAssignmentModal handleClose={handleWithdrawClose} />
				) : (
					<WithdrawPartnerAssignmentModal handleClose={handleWithdrawClose} />
				)
			) : null}
		</>
	);
}
